import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createGoogleSsoSchema, GoogleSsoFormData } from "../utils/ssoSchemas";
import { useConfigureGoogleSso } from "../hooks/sso-settings.hooks";

interface GoogleConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
}

export const GoogleConfigDialog: React.FC<GoogleConfigDialogProps> = ({
  open,
  onOpenChange,
  tenantId,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("sso.google.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("sso.google.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <GoogleConfigForm
            tenantId={tenantId}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const GoogleConfigForm: React.FC<{
  tenantId: string;
  onClose: () => void;
}> = ({ tenantId, onClose }) => {
  const { t } = useTranslation();
  const [showSecret, setShowSecret] = React.useState(false);
  const { mutate: configure, isPending } = useConfigureGoogleSso(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GoogleSsoFormData>({
    resolver: zodResolver(createGoogleSsoSchema()),
    defaultValues: {
      clientId: "",
      clientSecret: "",
    },
  });

  const onSubmit = (data: GoogleSsoFormData) => {
    configure(data, {
      onSuccess: () => {
        toast.success(t("sso.google.saveSuccess"));
        onClose();
      },
      onError: () => toast.error(t("sso.google.saveError")),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="google-client-id">
          {t("sso.google.clientIdLabel")} *
        </Label>
        <Input
          id="google-client-id"
          placeholder={t("sso.google.clientIdPlaceholder")}
          autoComplete="off"
          {...register("clientId")}
        />
        {errors.clientId ? (
          <p className="text-destructive text-xs">{errors.clientId.message}</p>
        ) : (
          <p className="text-muted-foreground text-xs">
            {t("sso.google.clientIdHelp")}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="google-client-secret">
          {t("sso.google.clientSecretLabel")} *
        </Label>
        <div className="relative">
          <Input
            id="google-client-secret"
            type={showSecret ? "text" : "password"}
            placeholder={t("sso.google.clientSecretPlaceholder")}
            autoComplete="off"
            className="pr-10"
            {...register("clientSecret")}
          />
          <button
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            aria-label={
              showSecret ? t("sso.card.hideSecret") : t("sso.card.showSecret")
            }
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
          >
            {showSecret ? (
              <EyeOff className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </button>
        </div>
        {errors.clientSecret ? (
          <p className="text-destructive text-xs">
            {errors.clientSecret.message}
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">
            {t("sso.google.clientSecretHelp")}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("sso.actions.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending ? t("sso.actions.saving") : t("sso.actions.save")}
        </Button>
      </div>
    </form>
  );
};
